import { useCallback, useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../types";

interface GoalEventsBySide {
  home: MatchEvent[];
  away: MatchEvent[];
}

interface LiveScore {
  home: number;
  away: number;
}

/**
 * Calculate the point value of a scoring event based on its shot_type.
 * 3pt → 3, ft → 1, everything else (2pt, Standard, etc.) → 2.
 */
const pointsForEvent = (event: MatchEvent): number => {
  const shotType = (event.data?.shot_type ?? "").toLowerCase();
  if (shotType === "3pt") return 3;
  if (shotType === "ft") return 1;
  return 2;
};

export interface UseLiveScoreResult {
  /** Kept as `goalEvents` to avoid breaking ScoreBoard / LoggerCockpit props */
  goalEvents: GoalEventsBySide;
  liveScore: LiveScore;
  formatGoalLabel: (event: MatchEvent) => string;
}

export const useLiveScore = (
  match: Match | null,
  liveEvents: MatchEvent[],
): UseLiveScoreResult => {
  /** Scoring events: Shots with outcome "Made" or "AndOne" */
  const goalEvents = useMemo(() => {
    if (!match) return { home: [] as MatchEvent[], away: [] as MatchEvent[] };
    const homeIds = new Set(match.home_team.players.map((player) => player.id));
    const awayIds = new Set(match.away_team.players.map((player) => player.id));
    const baskets = liveEvents.filter(
      (event) =>
        event.type === "Shot" &&
        (event.data?.outcome === "Made" || event.data?.outcome === "AndOne"),
    );
    return {
      home: baskets.filter((event) => homeIds.has(event.player_id ?? "")),
      away: baskets.filter((event) => awayIds.has(event.player_id ?? "")),
    };
  }, [liveEvents, match]);

  /** Aggregate actual basketball points (2PT=2, 3PT=3, FT=1) */
  const liveScore = useMemo(() => {
    if (!match) return { home: 0, away: 0 };
    const sumPoints = (events: MatchEvent[]) =>
      events.reduce((total, ev) => total + pointsForEvent(ev), 0);
    return {
      home: sumPoints(goalEvents.home),
      away: sumPoints(goalEvents.away),
    };
  }, [goalEvents, match]);

  const formatGoalLabel = useCallback(
    (event: MatchEvent) => {
      if (!match) return `${event.match_clock}`;
      const player =
        match.home_team.players.find((p) => p.id === event.player_id) ||
        match.away_team.players.find((p) => p.id === event.player_id);
      const playerLabel = player
        ? `#${player.jersey_number} ${player.full_name}`
        : event.player_id ?? "";
      const pts = pointsForEvent(event);
      return `${event.match_clock} · ${playerLabel} (+${pts})`;
    },
    [match],
  );

  return {
    goalEvents,
    liveScore,
    formatGoalLabel,
  };
};
